import { useState, useSyncExternalStore } from 'react'

/**
 * The name an anonymous visitor types for themselves in the comment form on a
 * shared project. Kept per browser rather than per project: it is the same
 * person wherever they were handed a link, and re-asking on each one is the
 * thing this is here to stop.
 *
 * The single point of contact with storage for this value — components ask the
 * hook, nothing reaches for the key itself.
 *
 * Every mounted consumer reads the same value. That is the point rather than a
 * detail: the thread reply editor can be on screen before the guest ever names
 * themselves in the comment form, and a per-instance copy read once on mount
 * would still be empty when they reply, sending an unnamed comment right after
 * they gave a name.
 *
 * The cap matches the read side: `toGuestName` in octopus-public-api caps
 * `guest_name` at 100, so what a guest types is what every later reader sees.
 *
 * On reaching for `localStorage` directly: the project has no shared storage
 * layer to go through — `store_old/middleware/localStorage.js` only clears the
 * auth token on logout — and a hook owning one key is the shape already used by
 * `useSpyMode`, `useShowResolvedState` and `useInfoBanner`. `CommentsForm`, the
 * form this name is typed into, keeps its comment draft the same way.
 */
const GUEST_NAME_KEY = 'guest_name'

export const GUEST_NAME_MAX = 100

const normalize = (value: string) => value.trim().slice(0, GUEST_NAME_MAX)

const listeners = new Set<() => void>()

const subscribe = (onChange: () => void) => {
  listeners.add(onChange)

  return () => {
    listeners.delete(onChange)
  }
}

// read every time rather than cached: the snapshot is a string, so equal
// values compare equal and this settles without an extra render
const getSnapshot = () => normalize(localStorage.getItem(GUEST_NAME_KEY) ?? '')

const store = (value: string) => {
  if (value === '') {
    localStorage.removeItem(GUEST_NAME_KEY)
  } else {
    localStorage.setItem(GUEST_NAME_KEY, value)
  }

  listeners.forEach((onChange) => {
    onChange()
  })
}

export const useGuestName = () => {
  const stored = useSyncExternalStore(subscribe, getSnapshot)
  // a draft exists only while this particular form is being typed into;
  // everywhere else the value is whatever was committed last
  const [draft, setDraft] = useState<string | null>(null)

  const guestName = draft ?? stored

  const setGuestName = (next: string) => {
    setDraft(next)
  }

  /** Persist what the guest typed and return the name as it will be stored. */
  const commitGuestName = (): string => {
    const value = normalize(guestName)

    store(value)
    setDraft(null)

    return value
  }

  return { commitGuestName, guestName, setGuestName }
}
